import { ROOT_NODE, useNode, type Node, type NodeHelpersType } from '@craftjs/core';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import { Resizer } from '../Resizer';
import SharedForm, {
  defaultProps,
  type FormProps,
  getFormNodeStylesObject,
} from '@builder4/shared/components/Form';
import { getVisibilityClasses, DEFAULT_VISIBILITY } from '@builder4/shared/utils/styleHelpers';
import clsx from 'clsx';
import { isFormSubNode } from '@builder4/shared/components/Form/formRegistry';
import { InspectorForm } from '@/components/inspector/InspectorForm/InspectorForm';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { FORM_NODE_NAME } from '@/utils/constants';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppSelector } from '@/store/hooks';
import { selectEffectiveBreakpoint } from '@/store/system/selectors';
import { removeUndefined } from '@/utils/helperFunc';

// Wrapper providing Resizer and Craft integration
const EditorForm = (props: Partial<FormProps> & { children?: React.ReactNode }) => {
  props = { ...defaultProps, ...props };
  const breakpoint = useAppSelector(selectEffectiveBreakpoint);
  const { getBPProp } = useBreakpointAwareProp(FORM_NODE_NAME);
  const { connectors } = useNode();
  const { isEditable, environment } = useRenderContext();
  const { visibility = DEFAULT_VISIBILITY } = props;
  const maxWidth = getBPProp(props, 'maxWidth');
  const fillSpace = getBPProp(props, 'fillSpace');

  const { mobile, tablet, ...computedStyles } = getFormNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(mobile) };
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(tablet) };
  }

  const visibilityClasses = getVisibilityClasses(visibility);
  const disablePointerEvents = isEditable && environment === 'builder';

  return (
    <Resizer
      className={clsx(
        'form-node',
        disablePointerEvents && 'pointer-events-none',
        visibilityClasses,
        { 'flex-shrink-1-imp': fillSpace === 'yes' },
      )}
      propKey={{ width: 'width', height: 'height' }}
      onRef={(ref) => {
        if (ref) {
          connectors.connect(connectors.drag(ref));
        }
      }}
      style={currentBpFlexStyle}
      maxWidth={maxWidth}
    >
      <SharedForm {...props}>{props.children}</SharedForm>
    </Resizer>
  );
};

EditorForm.craft = {
  ...SharedForm.craft,
  related: {
    toolbar: InspectorForm,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
    canMoveIn: (incomingNodes: Node[]) => {
      if (!incomingNodes || incomingNodes.length === 0) return false;
      return incomingNodes.every((incomingNode) => {
        const name = incomingNode.data.displayName;
        return isFormSubNode(name);
      });
    },
  },
};

export default EditorForm;
